import { useEffect, useState } from 'react';

import { Icon } from '~/components';
import { cn } from '~/lib/cn';

export interface QuickListProps {
  className?: string;
  values: (string | JSX.Element)[];
}

function textContent(elem: JSX.Element | string): string {
  if (!elem) {
    return '';
  }
  if (typeof elem === 'string') {
    return elem;
  }
  const children = (
    elem.props as { children: JSX.Element | JSX.Element[] | string }
  )?.children;
  if (children instanceof Array) {
    return children.map(textContent).join('');
  }
  return textContent(children);
}

const QuickList = (props: QuickListProps): JSX.Element => {
  const [search, setSearch] = useState<string>('');
  const [values, setValues] = useState<(string | JSX.Element)[]>(props.values);

  useEffect(() => {
    setValues(
      props.values.filter((value) => {
        const txt: string =
          typeof value === 'string' ? value : textContent(value);
        return txt.toLowerCase().includes(search.toLowerCase());
      }),
    );
  }, [search, props.values]);

  return (
    <div
      className={cn(
        'bg-white rounded-sm border p-sm border-emphasis-subtle',
        props.className,
      )}
    >
      <div className="relative flex items-center">
        <Icon
          className={cn('absolute left-sm w-[1em] text-subtle')}
          type="search"
          size="sm"
        />
        <input
          className="w-full rounded-sm border p-sm pl-lg text-body-sm bg-level-03 border-emphasis-subtle focus:outline-none"
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Type to check a value"
          type="text"
          value={search}
        />
        {search ? (
          <button
            className={cn('absolute right-sm text-subtle')}
            onClick={() => setSearch('')}
          >
            <Icon className="w-[1em]" type="faXmark" />
          </button>
        ) : null}
      </div>
      {values.length === 0 && (
        <div className="p-sm text-body-xs text-subtle">No results found...</div>
      )}
      {values.length > 0 && (
        <ul className="mt-sm h-[10em] overflow-auto text-body-xs">
          {values.map((value, i) => {
            return (
              <li
                className="p-sm"
                key={i}
                data-testid={`quicklist-item-${value}`}
              >
                {value}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

QuickList.displayName = 'QuickList';

export { QuickList };
